/**
 * WidgetErrorState — centred, branded error display for dashboard widgets.
 *
 * Replaces the previous left-aligned secondary-text error rendering
 * that every widget used inline. Two modes:
 *
 *   1. License-required mode (regex-detected from messages of the
 *      shape "pureoa license required" — the format the backend
 *      ``LicenseMiddleware`` emits on a 403). Shows a Lock icon,
 *      a humanised product label, and an "Upgrade plan" button
 *      that navigates to /system/billing where the tenant can
 *      actually do something about it.
 *
 *   2. Generic error mode. Lock icon swapped for a warning glyph,
 *      and an optional "Try again" button if the caller passes
 *      onRetry.
 *
 * The component fills the widget frame (height: 100%) and centres
 * its content both axes — previous inline rendering shrank into
 * the top-left and looked broken next to its neighbours.
 */

import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  ErrorOutlineOutlined,
  LockOutlined,
  RocketLaunchOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Props {
  /** The error message string (typically ApiError.message). */
  error: string;
  /** Optional retry handler; renders a "Try again" button if set. */
  onRetry?: () => void;
}

// Backend LicenseMiddleware emits "<product> license required" (legacy)
// or the new structured shape "<Product Label> is not included in your
// current plan..." (2026-05-19+). Both supported here.
const LICENSE_RE = /^(\w+)\s+license\s+required\.?$/i;
const LICENSE_NEW_RE = /^(QuTime|PureOA)/;

const PRODUCT_LABELS: Record<string, string> = {
  qutime: 'QuTime (Attendance)',
  pureoa: 'PureOA (Processes)',
};

const WidgetErrorState: React.FC<Props> = ({ error, onRetry }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const m = error.match(LICENSE_RE);
  const newM = error.match(LICENSE_NEW_RE);

  if (m || newM) {
    // Extract product code: legacy regex captures it as group 1;
    // new structured message starts with the brand name.
    const product = m
      ? m[1].toLowerCase()
      : newM![1].toLowerCase();
    const label = PRODUCT_LABELS[product] ?? product.toUpperCase();

    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 2,
          py: 2,
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: 'rgba(46,101,212,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LockOutlined sx={{ fontSize: 18, color: 'primary.main' }} />
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {t('common:license.notInPlan', { label })}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ maxWidth: 240, lineHeight: 1.45 }}
        >
          {t('common:license.upgradeBody', { module: label.split(' ')[0] })}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<RocketLaunchOutlined sx={{ fontSize: 16 }} />}
          onClick={() => navigate('/system/billing')}
          sx={{
            mt: 0.5,
            textTransform: 'none',
            fontSize: '0.8125rem',
            fontWeight: 600,
            borderRadius: 1.5,
            boxShadow: 'none',
          }}
        >
          {t('common:license.upgradePlan')}
        </Button>
      </Box>
    );
  }

  // Generic error fallback
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 2,
        py: 2,
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          bgcolor: 'rgba(244,67,54,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ErrorOutlineOutlined sx={{ fontSize: 18, color: 'error.main' }} />
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
        {t('common:license.couldNotLoad')}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ maxWidth: 280, lineHeight: 1.45, wordBreak: 'break-word' }}
      >
        {error}
      </Typography>
      {onRetry && (
        <Button
          variant="text"
          size="small"
          startIcon={<RefreshOutlined sx={{ fontSize: 16 }} />}
          onClick={onRetry}
          sx={{
            mt: 0.5,
            textTransform: 'none',
            fontSize: '0.8125rem',
            fontWeight: 600,
          }}
        >
          {t('common:license.tryAgain')}
        </Button>
      )}
    </Box>
  );
};

export default WidgetErrorState;
